import { useCallback, useEffect, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/PersonAddAlt1Outlined"
import BlockIcon from "@mui/icons-material/BlockOutlined"
import KeyIcon from "@mui/icons-material/KeyOutlined"
import SendIcon from "@mui/icons-material/ForwardToInboxOutlined"
import RestoreIcon from "@mui/icons-material/RestoreFromTrashOutlined"
import PeopleIcon from "@mui/icons-material/GroupsOutlined"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined"

import { userApi, clientApi, authApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import UserDialog from "./users/UserDialog"
import ChangeRoleDialog from "./users/ChangeRoleDialog"
import { formatDate } from "../utils/format"
import UserAvatar from "../components/UserAvatar"
import { rise } from "../motion"

/* Derived on the server from `active` and whether a password was ever set — see
   utils/accountStatus.js. Mapped here rather than sent as display text so the
   wording is the frontend's to change. */
const STATUS_LABELS = { invited: "Invited", active: "Active", deactivated: "Deactivated" }
const STATUS_COLORS = { invited: "warning", active: "success", deactivated: "default" }

export default function UsersPage() {
  const { can, isBackOffice } = usePermissions()

  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [clients, setClients] = useState([])
  const [clientFilter, setClientFilter] = useState("")
  const [search, setSearch] = useState("")
  /* The unfiltered size, remembered from whichever loads had no filters on.
     Filtering happens on the server, so a filtered response cannot say what it
     filtered out — and "12 of 40" is the sentence that makes a search result
     mean something. */
  const [total, setTotal] = useState(null)
  const [overall, setOverall] = useState(null)
  const [roleFor, setRoleFor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // { severity, message }. An invite that never left is not good news, and a
  // green banner saying "invited" would be the same lie the API used to tell.
  const [notice, setNotice] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)

  const filtersActive = Boolean(clientFilter) || Boolean(search.trim())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (clientFilter) params.client_id = clientFilter
      if (search.trim()) params.q = search.trim()
      const data = await userApi.list(params)
      const list = data.users || []
      setUsers(list)
      /*
       * The server's count, never the page's length.
       *
       * This used to do `setTotal(list.length)` when nothing was filtered, which
       * quietly substitutes the page size for the truth: the endpoint returns
       * fifty rows at a time, so a platform with a hundred and forty accounts
       * said "50 people" and showed fifty of them with nothing to suggest the
       * other ninety existed. On the screen an administrator uses to find
       * somebody, that is not a cosmetic difference.
       */
      const matching = typeof data.total === "number" ? data.total : list.length
      setTotal(matching)
      /*
       * Two counts, because "0 of 140" needs both and they are different
       * questions: how many match what is being asked for, and how many there
       * are altogether. The server's total is the first — it counts the same
       * WHERE clause the page was fetched with — so the second has to be
       * remembered from a load with nothing filtered, or a search matching
       * nothing reads "0 of 0" and says nothing at all.
       */
      if (!clientFilter && !search.trim()) setOverall(matching)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [clientFilter, search])

  useEffect(() => {
    // Debounced: typing in the search box should not be one request per keystroke.
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  useEffect(() => {
    authApi
      .roles()
      .then((data) => setRoles(data.roles || []))
      .catch(() => setRoles([]))

    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
  }, [isBackOffice, can])

  /**
   * Changing somebody's role signs them out.
   *
   * The server does it — the client renders its navigation from the grants it got
   * at login, so without it the person keeps seeing screens they no longer have.
   * Saying so afterwards stops it being reported as a bug.
   */
  const assignRole = async (userId, roleId) => {
    try {
      const result = await userApi.assignRole(userId, roleId === "" ? null : Number(roleId))
      setUsers((current) => current.map((u) => (u.id === userId ? result.user : u)))
      setRoleFor(null)
      setNotice({
        severity: "success",
        message:
          result.sessions_revoked > 0
            ? "Role updated. They were signed out so the change takes effect."
            : "Role updated.",
      })
    } catch (err) {
      // The dialog stays open: the choice that failed is still in it, and
      // reopening it to pick the same role again is a punishment for a server error.
      setError(err.userMessage)
    }
  }

  /**
   * Send them whatever their account is waiting on.
   *
   * The server decides which email that is, from the account's status — an
   * invite for somebody who never set a password, a reset link for somebody who
   * did. The reply says which it sent, so the banner reports what happened
   * rather than what was asked for.
   */
  const resend = async (user) => {
    try {
      const result = await userApi.resend(user.id)
      if (result.email_sent) {
        setNotice({ severity: "success", message: result.message })
      } else if (!result.email_configured) {
        setNotice({
          severity: "warning",
          message: `Nothing was sent to ${result.email} — email is not configured on this deployment.`,
        })
      } else {
        setNotice({ severity: "warning", message: result.message })
      }
    } catch (err) {
      setError(err.userMessage)
    }
  }

  const activate = async (user) => {
    try {
      const result = await userApi.activate(user.id)
      setNotice({ severity: "success", message: result.message })
      load()
    } catch (err) {
      setError(err.userMessage)
    }
  }

  /*
   * Break-glass, for when mail is down and somebody has to get in anyway.
   *
   * Still a browser prompt, and deliberately not dressed up as a form: a
   * password typed here has to reach the person out of band, which is the thing
   * the invite exists to avoid. Making it comfortable would be encouraging it.
   */
  const setPasswordFor = async (user) => {
    const password = window.prompt(
      `Set a password for ${user.username} directly (at least 10 characters).\n\nPrefer the resend button — it emails them a link only they can use.`,
    )
    if (!password) return
    try {
      await userApi.resetPassword(user.id, password)
      setNotice({ severity: "success", message: "Password set. They were signed out of every device." })
    } catch (err) {
      setError(err.userMessage)
    }
  }

  const deactivate = async (user) => {
    try {
      await userApi.deactivate(user.id)
      setNotice({ severity: "success", message: `${user.name || user.email} was deactivated.` })
      load()
    } catch (err) {
      setError(err.userMessage)
    }
  }

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={isBackOffice ? "Every account on the platform." : "The people in your organization."}
        actions={
          can("users", "create") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Add user
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      ) : null}

      <Card sx={{ p: "12px 14px", mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-end">
          <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, username or email" sx={{ minWidth: 240 }} />
          {isBackOffice && clients.length > 0 ? (
            <TextField select size="small" label="Organization" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} sx={{ minWidth: 200 }} slotProps={{ select: { displayEmpty: true } }}>
              <MenuItem value="">All organizations</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Box sx={{ flex: 1 }} />
          {filtersActive ? (
            <Button
              size="small"
              onClick={() => {
                setSearch("")
                setClientFilter("")
              }}
            >
              Clear filters
            </Button>
          ) : null}
          {/* The count is what tells you the filter did something. Without it a
              search that matches nothing and a request that failed both leave
              the same blank table. */}
          {/* Truncation has to be visible. "50 people" on a list of 140 is not
              a smaller number, it is a wrong one — and the only way anybody
              finds out is by looking for somebody who is not there. */}
          <Typography
            variant="caption"
            color={!filtersActive && total !== null && users.length < total ? "warning.dark" : "text.secondary"}
            sx={{ pb: 0.5 }}
          >
            {filtersActive
              ? `${total ?? users.length} of ${overall ?? total ?? users.length} people` +
                (total !== null && users.length < total ? ` — showing the first ${users.length}` : "")
              : total !== null && users.length < total
                ? `${users.length} of ${total} people — search to find the rest`
                : `${users.length} ${users.length === 1 ? "person" : "people"}`}
          </Typography>
        </Stack>
      </Card>

      <Card>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <LoadingRows rows={6} />
          </Box>
        ) : users.length === 0 ? (
          <EmptyState icon={PeopleIcon} title="No users" description="Nobody matches this search." />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  {isBackOffice ? <TableCell>Organization</TableCell> : null}
                  <TableCell sx={{ minWidth: 190 }}>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => {
                  /* Id 1 is the account the schema seeds to own rows nobody
                     should own. It has no mailbox and cannot be signed into, so
                     offering to email it is offering something the server
                     refuses. */
                  const isSystemAccount = user.id === 1

                  return (
                    <TableRow key={user.id} hover sx={{ opacity: user.active ? 1 : 0.55, ...rise(index) }}>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <UserAvatar
                            name={user.name}
                            email={user.email}
                            avatarKey={user.avatar_key}
                            size={32}
                            tooltip={false}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            {/* The name is the way in. A row full of icon
                                buttons for everything except "look at this
                                person" was the one thing people tried first. */}
                            <Typography
                              component={RouterLink}
                              to={`/users/${user.id}`}
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{
                                display: "block",
                                color: "text.primary",
                                textDecoration: "none",
                                borderRadius: 0.5,
                                "&:hover": { color: "primary.dark", textDecoration: "underline" },
                                "&:focus-visible": {
                                  outline: (theme) => `2px solid ${theme.palette.primary.dark}`,
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {user.name || user.username}
                            </Typography>
                            {/* The username is what they sign in with, and an
                                address may be on several accounts — so the row
                                needs it to identify anyone at all. */}
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {user.username} · {user.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {isBackOffice ? (
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.organization_label}
                          </Typography>
                        </TableCell>
                      ) : null}

                      {/* Text, not a control. Somebody scanning this table is
                          reading, and a row you are reading should not be a row
                          you can change by brushing past it. */}
                      <TableCell>
                        {user.role_id ? (
                          <Typography variant="body2">{user.role_label}</Typography>
                        ) : (
                          <Chip
                            size="small"
                            label="No role — cannot reach anything"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {/* Three states, not two. "Active" on somebody who has
                              never set a password says they are fine when they
                              cannot sign in at all — and it is the one case where
                              resending an invite is the answer. */}
                          <Chip
                            size="small"
                            label={STATUS_LABELS[user.status] || (user.active ? "Active" : "Inactive")}
                            color={STATUS_COLORS[user.status] || "default"}
                            variant={user.status === "invited" ? "outlined" : "filled"}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {user.last_access_on ? formatDate(user.last_access_on) : "Never"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {can("users", "assign_role") && !isSystemAccount ? (
                            <Tooltip title="Change role">
                              <IconButton
                                size="small"
                                onClick={() => setRoleFor(user)}
                                aria-label={`Change role for ${user.username}`}
                              >
                                <AdminPanelSettingsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                          {/* One button, labelled for where the account actually
                              is. An administrator resending is answering "they
                              never got it"; which email that is, is ours to work
                              out, not theirs. */}
                          {can("users", "reset_password") && user.status !== "deactivated" && !isSystemAccount ? (
                            <Tooltip
                              title={
                                user.status === "invited"
                                  ? "Resend the invite to set a password"
                                  : "Email them a password reset link"
                              }
                            >
                              <IconButton size="small" onClick={() => resend(user)} aria-label={`Resend email to ${user.username}`}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                          {/* Kept for when mail is down and somebody has to get
                              in regardless. Second, and never the obvious one:
                              a password chosen here has to be passed along out
                              of band, which is the thing the invite avoids. */}
                          {can("users", "reset_password") && user.status === "active" && !isSystemAccount ? (
                            <Tooltip title="Set a password directly (only if email is unavailable)">
                              <IconButton
                                size="small"
                                aria-label={`Set a password for ${user.username}`}
                                onClick={() => setPasswordFor(user)}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                          {can("users", "delete") && user.active && !isSystemAccount ? (
                            <Tooltip title="Deactivate">
                              <IconButton size="small" onClick={() => deactivate(user)} aria-label={`Deactivate ${user.username}`}>
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                          {/* Deactivation is reversible by design, and until now
                              nothing in the app could reverse it. */}
                          {can("users", "update") && !user.active ? (
                            <Tooltip title="Reactivate">
                              <IconButton size="small" onClick={() => activate(user)} aria-label={`Reactivate ${user.username}`}>
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <ChangeRoleDialog
        open={Boolean(roleFor)}
        user={roleFor}
        roles={roles}
        onClose={() => setRoleFor(null)}
        onSave={assignRole}
      />

      <UserDialog
        open={createOpen}
        roles={roles}
        clients={clients}
        onClose={() => setCreateOpen(false)}
        onCreated={(result) => {
          setCreateOpen(false)
          load()
          /*
           * What the administrator needs to know is whether the person will
           * actually hear about the account. The account itself is on screen a
           * line later, so saying "created" adds nothing; saying "invited" when
           * no mail is configured is worse than saying nothing at all.
           */
          if (result.email_sent) {
            setNotice({ severity: "success", message: `Invite sent to ${result.user.email}.` })
          } else if (!result.email_configured) {
            setNotice({
              severity: "warning",
              message: `${result.user.username} was created, but no invite could be sent — email is not configured on this deployment. They can get a link from "Forgot password?" once it is.`,
            })
          } else if (result.email_error === "still_sending") {
            setNotice({ severity: "info", message: `${result.user.username} was created. The invite is still going out.` })
          } else {
            setNotice({
              severity: "warning",
              message: `${result.user.username} was created, but the invite failed to send: ${result.email_error}`,
            })
          }
        }}
      />
    </Box>
  )
}
